import { ArticleComponentResizer } from "@ext/article/Components/ArticleComponentResizer";
import t from "@ext/localization/locale/translate";
import type { GalleryItem } from "@ext/markdown/elements/gallery/logic/getGalleryItemRatio";
import { type GalleryView, resolveGalleryView } from "@ext/markdown/elements/gallery/model/gallery";
import galleryViews from "@ext/markdown/elements/gallery/render/views/galleryViews";
import { type ReactNode, useId } from "react";

interface GalleryFrameProps {
	view: GalleryView;
	items: GalleryItem[];
	scale?: string;
	onScaleChange?: (scale: string) => void;
	selected?: boolean;
	isPrint?: boolean;
	caption?: ReactNode;
	children: ReactNode;
}

const GalleryFrame = (props: GalleryFrameProps) => {
	const { view, items, scale, onScaleChange, selected, isPrint, caption, children } = props;
	const id = useId();
	const effectiveView = resolveGalleryView(view, isPrint);
	const { Component, getDefaultScale, getAspectRatio } = galleryViews[effectiveView];
	const ratios = items.map((item) => item.ratio);

	return (
		<figure
			aria-label={t("editor.gallery.name")}
			className="mb-[1em] break-inside-avoid [&>.resource-caption.visible]:mt-[0.5em]"
			data-component="gallery"
			data-gallery={id}
			data-gallery-view={effectiveView}
		>
			<ArticleComponentResizer
				aspectRatio={getAspectRatio?.(ratios)}
				className="[container-type:inline-size]"
				defaultScale={getDefaultScale(ratios)}
				disabled={!onScaleChange}
				isPrint={isPrint}
				onChange={onScaleChange}
				scale={scale}
				selected={selected}
			>
				<Component isPrint={isPrint} items={items} scope={`[data-gallery="${id}"]`}>
					{children}
				</Component>
			</ArticleComponentResizer>
			{caption}
		</figure>
	);
};

export default GalleryFrame;
